import { computed, type Ref } from 'vue'
import { autoUpdate, flip, offset, shift, useFloating, type ReferenceElement } from '@floating-ui/vue'
import type { CitationTarget } from '~/components/citation/types'

export interface PointerPosition { x: number, y: number }

/** Gap between the citation and the card. Most of it is the card wrapper's own padding, which stays
 *  hoverable so the pointer can cross the gap without dismissing the card. */
const HOVER_BRIDGE_PX = 2
const VIEWPORT_PADDING_PX = 8

function lineBoxes(el: HTMLElement): DOMRect[] {
  // jsdom has no layout and returns an empty list; every caller falls back to the bounding box.
  return typeof el.getClientRects === 'function' ? Array.from(el.getClientRects()) : []
}

/**
 * Which line box of a citation the pointer is over.
 *
 * `.marcus-cite` is `display: inline`, so a citation that wraps has one client rect per line and
 * `getBoundingClientRect()` returns their UNION — a box nearly as wide as the paragraph, whose centre
 * sits next to neither fragment. Anchoring to that union is what detached the card (MAR-483).
 *
 * Falls back to the first line box: the one the citation starts on, which is the right answer for
 * keyboard focus and for any pointer we cannot place.
 */
export function citationFragmentIndex(el: HTMLElement, pointer?: PointerPosition | null): number {
  if (!pointer) return 0
  const rects = lineBoxes(el)
  if (rects.length <= 1) return 0
  const hit = rects.findIndex(r =>
    pointer.y >= r.top && pointer.y <= r.bottom
    && pointer.x >= r.left - 1 && pointer.x <= r.right + 1)
  return hit === -1 ? 0 : hit
}

/** The live rect of one line box. Recomputed on every reposition, so the card follows the citation
 *  when the transcript scrolls or reflows underneath it. */
export function citationFragmentRect(el: HTMLElement, index: number): DOMRect {
  const rects = lineBoxes(el)
  return rects[index] ?? rects[0] ?? el.getBoundingClientRect()
}

/**
 * Positions the card against a single line box of the open citation.
 *
 * `flip` moves it above a citation near the bottom of the viewport and `shift` keeps it clear of the
 * left/right edges — the hand-rolled centre-clamp this replaced could only do the second.
 */
export function useCitationAnchor(
  target: Ref<CitationTarget | null>,
  floating: Ref<HTMLElement | null>,
) {
  // A virtual reference: Floating UI never needs the element, only a rect — which is what lets us hand
  // it one fragment of a wrapped citation instead of the whole element.
  const reference = computed<ReferenceElement | null>(() => {
    const open = target.value
    if (!open) return null
    return {
      getBoundingClientRect: () => citationFragmentRect(open.el, open.fragmentIndex),
      contextElement: open.el,
    }
  })

  const { floatingStyles } = useFloating(reference, floating, {
    placement: 'bottom',
    strategy: 'fixed',
    middleware: [
      offset(HOVER_BRIDGE_PX),
      flip({ padding: VIEWPORT_PADDING_PX }),
      shift({ padding: VIEWPORT_PADDING_PX }),
    ],
    whileElementsMounted: autoUpdate,
  })

  return { floatingStyles }
}
